/**
 * Zelfstandige HTML-snapshot van een gerenderd element, voor de PDF-renderer van de API
 * (headless Chrome zonder netwerk: alles moet in het document zelf zitten). Zie
 * docs/superpowers/specs/2026-10-21-chart-pdf-print-design.md §3.1.
 *
 * - CSS: alleen de regels die iets in de snapshot (of html/body) raken, uit alle stylesheets
 *   (globaal én component-stijlen). Hover/focus-regels vallen vanzelf weg.
 * - Fonts: alleen de `@font-face`-regels van fonts die de snapshot echt gebruikt, ingebed als
 *   data-URI; de renderer mag geen fonts ophalen.
 * - Geen scripts of event-attributen (de server dwingt dat via CSP ook af).
 */

export interface PrintSnapshotOptions {
  /** `lang`-attribuut van het document (bepaalt o.a. woordafbreking). */
  readonly lang: string;
  /** Documenttitel (PDF-metadata). */
  readonly title: string;
}

interface UsedFont {
  readonly family: string;
  readonly weight: number;
  readonly style: string;
}

interface FontFaceCandidate {
  readonly family: string;
  readonly weight: number;
  readonly style: string;
  readonly rule: CSSFontFaceRule;
  readonly baseUrl: string;
}

/** Vaste paginaregels; komen ná de verzamelde CSS zodat app-layout (vaste hoogtes, scrollvlakken) niet meedoet. */
const PAGE_CSS =
  '@page { size: A4; margin: 0; } ' +
  'html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; ' +
  'height: auto !important; min-height: 0 !important; min-width: 0 !important; overflow: visible !important; }';

const PSEUDO_ELEMENT = /::?(before|after|first-line|first-letter|marker|placeholder|selection|backdrop|file-selector-button|-webkit-[\w-]+|-moz-[\w-]+)/g;

export async function buildPrintSnapshot(root: HTMLElement, options: PrintSnapshotOptions): Promise<string> {
  // HTML, CSS en gebruikte fonts op hetzelfde moment vastleggen, vóór er iets ge-await wordt:
  // tijdens het ophalen van de fonts kan het blad nog renderen (bv. de center-keynotes), en dan
  // stonden er elementen in de HTML waarvan de CSS ontbrak.
  const body = sanitizedClone(root).outerHTML;
  const css = collectCss(root);
  const usedFonts = collectUsedFonts(root);
  const fontFaces = await embedFontFaces(selectFontFaces(usedFonts, css.fontFaces));

  return (
    `<!doctype html><html lang="${escapeAttribute(options.lang)}"><head><meta charset="utf-8">` +
    `<title>${escapeText(options.title)}</title>` +
    `<style>${fontFaces.join('\n')}</style>` +
    `<style>${css.rules.join('\n')}</style>` +
    `<style>${PAGE_CSS}</style>` +
    `</head><body>${body}</body></html>`
  );
}

// ── CSS ────────────────────────────────────────────────────────────────────

function collectCss(root: HTMLElement): { rules: string[]; fontFaces: FontFaceCandidate[] } {
  const rules: string[] = [];
  const fontFaces: FontFaceCandidate[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    const sheetRules = readRules(sheet);
    if (sheetRules == null) continue;
    const baseUrl = sheet.href ?? document.baseURI;
    rules.push(...filterRules(sheetRules, root, fontFaces, baseUrl));
  }
  return { rules, fontFaces };
}

/** `cssRules` gooit bij stylesheets van een ander domein; die kunnen we toch niet meenemen. */
function readRules(sheet: CSSStyleSheet): CSSRuleList | null {
  try {
    return sheet.cssRules;
  } catch {
    return null;
  }
}

function filterRules(list: CSSRuleList, root: HTMLElement, fontFaces: FontFaceCandidate[], baseUrl: string): string[] {
  const kept: string[] = [];
  for (const rule of Array.from(list)) {
    if (rule instanceof CSSStyleRule) {
      if (selectorMatches(rule.selectorText, root)) kept.push(rule.cssText);
    } else if (rule instanceof CSSFontFaceRule) {
      const face = toFontFaceCandidate(rule, baseUrl);
      if (face != null) fontFaces.push(face);
    } else if (rule instanceof CSSMediaRule) {
      const inner = filterRules(rule.cssRules, root, fontFaces, baseUrl);
      if (inner.length > 0) kept.push(`@media ${rule.conditionText} {\n${inner.join('\n')}\n}`);
    } else if (rule instanceof CSSSupportsRule) {
      const inner = filterRules(rule.cssRules, root, fontFaces, baseUrl);
      if (inner.length > 0) kept.push(`@supports ${rule.conditionText} {\n${inner.join('\n')}\n}`);
    } else if (rule instanceof CSSImportRule) {
      const imported = rule.styleSheet != null ? readRules(rule.styleSheet) : null;
      if (imported != null) kept.push(...filterRules(imported, root, fontFaces, rule.styleSheet?.href ?? baseUrl));
    }
    // @keyframes, @page, @layer-statements e.d.: niet nodig voor een stilstaande printpagina.
  }
  return kept;
}

/**
 * Raakt de selector iets in de snapshot, of html/body (CSS-variabelen op :root, basisfont)?
 * Pseudo-elementen eruit (die matchen nooit via querySelector); ongeldige selectors (vendor-
 * prefixen van andere browsers) tellen als geen match.
 */
function selectorMatches(selectorText: string, root: HTMLElement): boolean {
  const selector = selectorText.replace(PSEUDO_ELEMENT, '').trim();
  if (selector === '') return false;
  try {
    return (
      root.matches(selector) ||
      root.querySelector(selector) != null ||
      document.documentElement.matches(selector) ||
      document.body.matches(selector)
    );
  } catch {
    return false;
  }
}

// ── Fonts ──────────────────────────────────────────────────────────────────

function toFontFaceCandidate(rule: CSSFontFaceRule, baseUrl: string): FontFaceCandidate | null {
  const family = normalizeFamily(rule.style.getPropertyValue('font-family'));
  if (family === '') return null;
  return {
    family,
    weight: parseWeight(rule.style.getPropertyValue('font-weight')),
    style: normalizeStyle(rule.style.getPropertyValue('font-style')),
    rule,
    baseUrl,
  };
}

/**
 * Fonts van elementen met eigen tekst (ook SVG-tekst) én van `::before`/`::after` met inhoud:
 * icoonfonts (Font Awesome) tekenen enkel via pseudo-elementen en vielen anders weg. Elke familie
 * uit de stack telt mee.
 */
function collectUsedFonts(root: HTMLElement): UsedFont[] {
  const used = new Map<string, UsedFont>();
  const add = (computed: CSSStyleDeclaration): void => {
    const weight = parseWeight(computed.fontWeight);
    const style = normalizeStyle(computed.fontStyle);
    for (const family of computed.fontFamily.split(',').map(normalizeFamily)) {
      if (family === '') continue;
      used.set(`${family}|${weight}|${style}`, { family, weight, style });
    }
  };
  const elements = [root, ...Array.from(root.querySelectorAll('*'))];
  for (const element of elements) {
    if (hasOwnText(element)) add(getComputedStyle(element));
    for (const pseudo of ['::before', '::after']) {
      const computed = getComputedStyle(element, pseudo);
      if (computed.content !== 'none' && computed.content !== 'normal' && computed.content !== '""') add(computed);
    }
  }
  return Array.from(used.values());
}

function hasOwnText(element: Element): boolean {
  return Array.from(element.childNodes).some(
    node => node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== '',
  );
}

/** Per gebruikt (familie, gewicht, stijl) de face die de browser zou kiezen: zelfde stijl, dichtste gewicht. */
function selectFontFaces(used: readonly UsedFont[], candidates: readonly FontFaceCandidate[]): FontFaceCandidate[] {
  const selected = new Set<FontFaceCandidate>();
  for (const font of used) {
    const sameFamily = candidates.filter(candidate => candidate.family === font.family);
    if (sameFamily.length === 0) continue;
    const sameStyle = sameFamily.filter(candidate => candidate.style === font.style);
    const pool = sameStyle.length > 0 ? sameStyle : sameFamily;
    const best = pool.reduce((a, b) => {
      const da = Math.abs(a.weight - font.weight);
      const db = Math.abs(b.weight - font.weight);
      return db < da || (db === da && b.weight > a.weight) ? b : a;
    });
    selected.add(best);
  }
  return Array.from(selected);
}

async function embedFontFaces(faces: readonly FontFaceCandidate[]): Promise<string[]> {
  const embedded = await Promise.all(faces.map(embedFontFace));
  return embedded.filter((css): css is string => css != null);
}

async function embedFontFace(face: FontFaceCandidate): Promise<string | null> {
  const source = pickFontSource(face.rule.style.getPropertyValue('src'));
  if (source == null) return null;
  const response = await fetch(new URL(source.url, face.baseUrl).href);
  if (!response.ok) return null;
  const dataUrl = await blobToDataUrl(await response.blob());
  const format = source.format != null ? ` format("${source.format}")` : '';
  return (
    `@font-face { font-family: "${face.rule.style.getPropertyValue('font-family').replace(/["']/g, '').trim()}"; ` +
    `src: url("${dataUrl}")${format}; font-weight: ${face.weight}; font-style: ${face.style}; }`
  );
}

/** Eerste woff2-bron, anders de eerste url() uit `src`. */
function pickFontSource(src: string): { url: string; format: string | null } | null {
  const sources = Array.from(src.matchAll(/url\(\s*["']?([^"')]+)["']?\s*\)(?:\s*format\(\s*["']?([^"')]+)["']?\s*\))?/g)).map(
    match => ({ url: match[1], format: match[2] ?? null }),
  );
  return sources.find(source => source.format === 'woff2') ?? sources[0] ?? null;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function normalizeFamily(family: string): string {
  return family.replace(/["']/g, '').trim().toLowerCase();
}

function normalizeStyle(style: string): string {
  return style.trim() === '' || style.trim() === 'normal' ? 'normal' : style.trim();
}

/** `normal`/`bold`/`700`/een bereik `100 900` → één getal (begin van het bereik). */
function parseWeight(weight: string): number {
  const value = weight.trim();
  if (value === '' || value === 'normal') return 400;
  if (value === 'bold') return 700;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 400 : parsed;
}

// ── DOM ────────────────────────────────────────────────────────────────────

function sanitizedClone(root: HTMLElement): HTMLElement {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('script, iframe, object, embed').forEach(element => element.remove());
  for (const element of [clone, ...Array.from(clone.querySelectorAll('*'))]) {
    for (const attribute of Array.from(element.attributes)) {
      if (attribute.name.toLowerCase().startsWith('on')) element.removeAttribute(attribute.name);
    }
  }
  return clone;
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
